/* eslint-disable import/no-unresolved */
import React, { useEffect, useMemo } from 'react';

import RightModalLayout from '../../../../../../../layouts/Modal/RightModalLayout';
import CardMC from '../../../../../../../components/CardMC';
import IconMC from '../../../../../../../components/IconMC';
import useGlobalInstructionToolsModal from '../hooks/useGlobalInstructionToolsModal';
import { AgentProvider } from '../../AgentContext';
import AssignToTeamToolView from './toolModalViews/AssignToTeamToolView';
import CreateNegotiationDetailsToolView from './toolModalViews/CreateNegotiationDetailsToolView';
import MoveNegotiationDetailsToolView from './toolModalViews/MoveNegotiationDetailsToolView';
import SendFileToolView from './toolModalViews/SendFileToolView';
import TriggerWhatsappFlowToolView from './toolModalViews/TriggerWhatsappFlowToolView';
import PurchaseIntentionToolView from './toolModalViews/PurchaseIntentionToolView';
import RetrieveProductsToolView from './toolModalViews/RetrieveProductsToolView';
import RetailerApiRequestToolView from './toolModalViews/RetailerApiRequestToolView';
import GetCustomerCrmDataToolView from './toolModalViews/GetCustomerCrmDataToolView';

const GlobalInstructionToolsModalContent = ({
  onSelectTool,
  mode = 'select',
  initialToolId = null,
  initialArgs = null,
  submitToolLabel = null
}) => {
  const {
    t,
    tools,
    closeAction,
    handleSelect,
    currentView,
    selectedTool,
    selectedTeam,
    teamOptions,
    setSelectedTeam,
    selectedFunnel,
    funnelOptions,
    setSelectedFunnel,
    selectedStep,
    setSelectedStep,
    purchaseIntentionAiDisabled,
    setPurchaseIntentionAiDisabled,
    retrieveProductsIncludeImages,
    setRetrieveProductsIncludeImages,
    mediaItems,
    mediaLoading,
    selectedFileIds,
    toggleFileId,
    handleConfirmSendFile,
    uploadingMedia,
    uploadNewMedia,
    whatsappFlowItems,
    whatsappFlowsLoading,
    selectedWhatsappFlowId,
    setSelectedWhatsappFlowId,
    handleConfirmTriggerWhatsappFlow,
    handleBack,
    handleConfirmAssignment,
    handleConfirmNegotiation,
    handleConfirmPurchaseIntention,
    handleConfirmRetrieveProducts,
    retailerApiRequestTool,
    crmFieldOptions,
    crmFieldsLoading,
    selectedCrmFieldIds,
    toggleCrmFieldId,
    handleConfirmGetCustomerCrmData
  } = useGlobalInstructionToolsModal({
    onSelectTool,
    mode,
    initialToolId,
    initialArgs
  });

  const footerSubmitLabel = submitToolLabel || t('tools.add_tool_button');
  const backOrClose = useMemo(
    () => ((mode === 'select' && initialToolId) ? closeAction : handleBack),
    [mode, initialToolId, closeAction, handleBack]
  );

  useEffect(() => {
    if (!initialToolId || currentView !== 'list') return;
    requestAnimationFrame(() => {
      const el = document.getElementById(`tool-row-${initialToolId}`);
      el?.scrollIntoView?.({ block: 'center' });
    });
  }, [currentView, initialToolId]);

  switch (currentView) {
    case 'assign_to_team':
      return (
        <AssignToTeamToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmAssignment={handleConfirmAssignment}
          teamOptions={teamOptions}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
        />
      );
    case 'create_negotiation_details':
      return (
        <CreateNegotiationDetailsToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmNegotiation={handleConfirmNegotiation}
          funnelOptions={funnelOptions}
          selectedFunnel={selectedFunnel}
          setSelectedFunnel={setSelectedFunnel}
          selectedStep={selectedStep}
          setSelectedStep={setSelectedStep}
        />
      );
    case 'move_negotiation_details':
      return (
        <MoveNegotiationDetailsToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmNegotiation={handleConfirmNegotiation}
          funnelOptions={funnelOptions}
          selectedFunnel={selectedFunnel}
          setSelectedFunnel={setSelectedFunnel}
          selectedStep={selectedStep}
          setSelectedStep={setSelectedStep}
        />
      );
    case 'purchase_intention_details':
    case 'create_order_details':
    case 'get_payment_link_details':
      return (
        <PurchaseIntentionToolView
          t={t}
          title={t(selectedTool?.nameKey || 'tools.purchase_intention_title')}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmPurchaseIntention={handleConfirmPurchaseIntention}
          purchaseIntentionAiDisabled={purchaseIntentionAiDisabled}
          setPurchaseIntentionAiDisabled={setPurchaseIntentionAiDisabled}
        />
      );
    case 'retrieve_products_details':
      return (
        <RetrieveProductsToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmRetrieveProducts={handleConfirmRetrieveProducts}
          retrieveProductsIncludeImages={retrieveProductsIncludeImages}
          setRetrieveProductsIncludeImages={setRetrieveProductsIncludeImages}
        />
      );
    case 'send_file':
      return (
        <SendFileToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmSendFile={handleConfirmSendFile}
          mediaItems={mediaItems}
          mediaLoading={mediaLoading}
          selectedFileIds={selectedFileIds}
          toggleFileId={toggleFileId}
          uploadingMedia={uploadingMedia}
          uploadNewMedia={uploadNewMedia}
        />
      );
    case 'trigger_whatsapp_flow':
      return (
        <TriggerWhatsappFlowToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmTriggerWhatsappFlow={handleConfirmTriggerWhatsappFlow}
          flowItems={whatsappFlowItems}
          flowsLoading={whatsappFlowsLoading}
          selectedWhatsappFlowId={selectedWhatsappFlowId}
          setSelectedWhatsappFlowId={setSelectedWhatsappFlowId}
        />
      );
    case 'retailer_api_request':
      return (
        <RetailerApiRequestToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          apiRequestTool={retailerApiRequestTool}
        />
      );
    case 'get_customer_crm_data':
      return (
        <GetCustomerCrmDataToolView
          t={t}
          footerSubmitLabel={footerSubmitLabel}
          handleBack={backOrClose}
          handleConfirmGetCustomerCrmData={handleConfirmGetCustomerCrmData}
          crmFieldOptions={crmFieldOptions}
          crmFieldsLoading={crmFieldsLoading}
          selectedCrmFieldIds={selectedCrmFieldIds}
          toggleCrmFieldId={toggleCrmFieldId}
        />
      );
    case 'list':
    default:
      break;
  }

  return (
    <RightModalLayout
      closeAction={closeAction}
      title={t('tools.modal_title')}
      showBackButton
    >
      <div className="tw-wrapper">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-mb-2">
            <div className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-1">
              {t('tools.list_title')}
            </div>
            <div className="tw-text-gray-600 tw-text-xs">
              {t('tools.list_description')}
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-3">
            {tools.map((tool, index) => {
              const isSelected = selectedTool?.id === tool.id;
              const isLocked = tool.isLocked === true
                || tool.requiresSubscription === true
                || tool.isAvailable === false;
              const previousTool = tools[index - 1];
              const previousLocked = previousTool && (
                previousTool.isLocked === true
                || previousTool.requiresSubscription === true
                || previousTool.isAvailable === false
              );
              const showLockedSeparator = isLocked && previousTool && !previousLocked;

              return (
                <React.Fragment key={tool.id}>
                  {showLockedSeparator && (
                    <div className="tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-my-3" />
                  )}
                  <CardMC
                    id={`tool-row-${tool.id}`}
                    className={
                      `tw-p-4 tw-flex tw-items-center tw-justify-between tw-transition-colors ${
                        isLocked
                          ? 'tw-cursor-not-allowed tw-bg-gray-50/60'
                          : `tw-cursor-pointer hover:tw-bg-gray-50 ${isSelected ? 'tw-bg-gray-50 !tw-border-gray-300' : ''}`
                      }`
                    }
                    onClick={isLocked ? undefined : () => handleSelect(tool)}
                  >
                    <div className="tw-flex tw-gap-4 tw-items-center tw-min-w-0">
                      <div
                        className={`tw-p-2 tw-rounded-lg tw-shrink-0 ${
                          isLocked ? 'tw-bg-gray-100' : tool.bgClass
                        }`}
                      >
                        <IconMC
                          name={tool.icon}
                          className={isLocked ? 'tw-text-gray-400' : tool.colorClass}
                        />
                      </div>
                      <div className="tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                          <span
                            className={`tw-font-bold tw-text-sm ${
                              isLocked ? 'tw-text-gray-500' : 'tw-text-gray-900'
                            }`}
                          >
                            {tool.title}
                          </span>
                          {tool.requiresSubscription === true ? (
                            <span className="tw-px-1.5 tw-py-0.5 tw-bg-orange-50 tw-text-orange-600 tw-text-[10px] tw-font-semibold tw-rounded tw-whitespace-nowrap">
                              {t('tools.requires_subscription_badge')}
                            </span>
                          ) : tool.isBeta ? (
                            <span className="tw-px-1.5 tw-py-0.5 tw-bg-purple-50 tw-text-purple-500 tw-text-[10px] tw-rounded">
                              {t('tools.beta_badge')}
                            </span>
                          ) : null}
                        </div>
                        {tool.description && (
                          <div className={`tw-text-xs ${isLocked ? 'tw-text-gray-400' : 'tw-text-gray-500'}`}>
                            {tool.description}
                          </div>
                        )}
                        {!isLocked && tool.note && (
                          <div className="tw-bg-blue-50 tw-border tw-border-blue-100 tw-flex tw-mt-2 tw-px-2 tw-py-1 tw-rounded-xl">
                            <span className="tw-flex tw-items-start tw-gap-1 tw-font-medium tw-text-[10px] tw-text-blue-500 tw-leading-snug">
                              <i className="mi-info-outline tw-text-[16px] tw-shrink-0"></i>
                              {tool.note}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLocked && (
                      <IconMC
                        name="chevron-right-fill"
                        className="tw-text-gray-400 tw-shrink-0"
                      />
                    )}
                  </CardMC>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </RightModalLayout>
  );
};

const GlobalInstructionToolsModal = (props) => (
  <AgentProvider agentId={props.agentId}>
    <GlobalInstructionToolsModalContent {...props} />
  </AgentProvider>
);

export default GlobalInstructionToolsModal;
